import { callFunction } from "./functions-client";

// Fleet-OS F8 — telematics / ELD-agnostic integration ("bring your own ELD"). Register a
// connection and ingest normalized HOS/GPS/engine records from any source (webhook, direct
// provider, or RODS-CSV import) into one store. Owner-private, web console.
export const ELD_PROVIDERS = ["manual", "rods_csv", "terminal", "truckercloud", "motive", "samsara", "geotab"] as const;
export type EldProvider = (typeof ELD_PROVIDERS)[number];

export const PROVIDER_LABELS: Record<EldProvider, string> = {
  manual: "Manual", rods_csv: "RODS CSV", terminal: "Terminal (aggregator)", truckercloud: "TruckerCloud (aggregator)",
  motive: "Motive", samsara: "Samsara", geotab: "Geotab",
};
/** Direct providers need OAuth we activate later; the rest work today. */
export const OAUTH_PROVIDERS: EldProvider[] = ["motive", "samsara", "geotab"];

export type RecordType = "position" | "hos" | "engine";

export interface EldConnection {
  id: string; provider: EldProvider; display_name: string | null; status: string;
  external_account_id: string | null; last_sync_at: string | null; created_at: string; record_count?: number;
}
export interface TelematicsRecord {
  id: string; connection_id: string; record_type: RecordType; vehicle_ref: string | null;
  driver_ref: string | null; occurred_at: string; lat: number | null; lng: number | null;
  speed: number | null; odometer: number | null; engine_hours: number | null; duty_status: string | null;
}
export interface TelematicsSummary {
  connections: Pick<EldConnection, "id" | "provider" | "display_name" | "status" | "last_sync_at">[];
  counts: Partial<Record<RecordType, number>>;
  last_sync_at: string | null;
}

export const connectEld = (input: { provider: EldProvider; display_name?: string; external_account_id?: string }) =>
  callFunction<{ connection: EldConnection; ingest_token: string; authorize: { required: boolean; message?: string } }>(
    "telematics/connect", { body: input });

export const listConnections = () =>
  callFunction<{ connections: EldConnection[] }>("telematics/connections", { method: "GET" });

export const rotateIngestToken = (id: string) =>
  callFunction<{ ingest_token: string }>("telematics/connection-token", { body: { id } });

export const disconnectEld = (id: string) =>
  callFunction<{ ok: boolean }>("telematics/disconnect", { body: { id } });

export const importRods = (connection_id: string, csv: string) =>
  callFunction<{ imported: number }>("telematics/import-rods", { body: { connection_id, csv } });

export const listRecords = (opts: { type?: RecordType; vehicle_ref?: string; since?: string; limit?: number } = {}) => {
  const p = new URLSearchParams();
  if (opts.type) p.set("type", opts.type);
  if (opts.vehicle_ref) p.set("vehicle_ref", opts.vehicle_ref);
  if (opts.since) p.set("since", opts.since);
  if (opts.limit) p.set("limit", String(opts.limit));
  const qs = p.toString();
  return callFunction<{ records: TelematicsRecord[] }>(`telematics/records${qs ? `?${qs}` : ""}`, { method: "GET" });
};

export const telematicsSummary = () =>
  callFunction<TelematicsSummary>("telematics/summary", { method: "GET" });
